import { breadcrumbJsonLd, breadcrumbsFor, productJsonLd, serviceJsonLd } from './jsonLd';
import { clamp } from './url';
import type { SeoInput } from './useSeo';

/** For pages whose content comes from the CMS: built once the data has arrived. */

export function productDetailSeo(input: {
  name: string;
  description: string | null;
  image?: string;
  path: string;
}): SeoInput {
  const description = clamp(input.description || `${input.name} from Sheel Waterproofing, Nepal.`);
  return {
    title: `${input.name} | Sheel Waterproofing Nepal`,
    description,
    path: input.path,
    image: input.image,
    imageAlt: input.name,
    jsonLd: [
      breadcrumbJsonLd(breadcrumbsFor(input.path, input.name)),
      productJsonLd({ name: input.name, description, path: input.path, image: input.image }),
    ],
  };
}

export function serviceDetailSeo(input: {
  name: string;
  description: string | null;
  image?: string;
  path: string;
}): SeoInput {
  const description = clamp(input.description || `${input.name} by Sheel Waterproofing in Nepal.`);
  return {
    title: `${input.name} in Nepal | Sheel Waterproofing`,
    description,
    path: input.path,
    image: input.image,
    imageAlt: input.name,
    jsonLd: [
      breadcrumbJsonLd(breadcrumbsFor(input.path, input.name)),
      serviceJsonLd({ name: input.name, description, path: input.path }),
    ],
  };
}

export function projectDetailSeo(input: {
  name: string;
  location?: string | null;
  client?: string | null;
  scopeOfWork?: string | null;
  image?: string;
  path: string;
}): SeoInput {
  const where = input.location ? ` in ${input.location}` : '';
  const fallback = `Waterproofing project${where} by Sheel Waterproofing: ${input.name}.`;
  const lead = `Waterproofing project${where}: ${input.name}.`;
  const description = clamp(
    input.scopeOfWork ? `${lead} ${input.scopeOfWork}` : fallback
  );
  return {
    title: `${input.name} | Sheel Waterproofing`,
    description,
    path: input.path,
    image: input.image,
    imageAlt: input.name,
    jsonLd: [breadcrumbJsonLd(breadcrumbsFor(input.path, input.name))],
  };
}

export function galleryCategorySeo(input: { name: string; path: string }): SeoInput {
  const title = `${input.name} — Photo Gallery | Sheel Waterproofing`;
  const description = clamp(
    `Photos and videos from Sheel Waterproofing project sites in Nepal, tagged “${input.name}”.`
  );
  return {
    title,
    description,
    path: input.path,
    jsonLd: [breadcrumbJsonLd(breadcrumbsFor(input.path, input.name))],
  };
}

/** A CMS page that does not exist. Marked noindex; a static host cannot answer with a real 404 here. */
export function notFoundSeo(what: string): SeoInput {
  return {
    title: `${what} not found | Sheel Waterproofing`,
    description: `This ${what.toLowerCase()} could not be found.`,
    noindex: true,
  };
}
